/*
 * Website search
 */
import api from "@/utils/axios";
import { API_BASE_URL } from "./apiBase";
import type { PagedResponse } from "@/types/pagination.types";
import { MAX_CATEGORY_PATH_SEGMENTS } from "@/constants/category";
import axios from "axios";
import { createRequestDedupCache } from "./requestDedupCache";

const SEARCH_PAGED_MAX_ATTEMPTS = 2;
const SEARCH_PAGED_RETRY_DELAY_MS = 350;
const RETRYABLE_SEARCH_STATUSES = new Set([408, 429, 500, 502, 503, 504]);
const sidePanelCountCacheOptions = {
  ttlMs: 20_000,
  maxEntries: 200,
  orphanAbortDelayMs: 150,
} as const;
const thematicCountsRequestCache = createRequestDedupCache(
  sidePanelCountCacheOptions
);
const regionCountsRequestCache = createRequestDedupCache(
  sidePanelCountCacheOptions
);

const isAbortLikeError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { code?: string; name?: string };
  return (
    candidate.code === "ERR_CANCELED" ||
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError"
  );
};

const waitWithAbort = async (ms: number, signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
};

const waitWithOptionalAbort = async (ms: number, signal?: AbortSignal) => {
  if (!signal) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });
    return;
  }
  await waitWithAbort(ms, signal);
};

const shouldRetryTransportError = (error: unknown): boolean => {
  if (isAbortLikeError(error)) {
    return false;
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (typeof status === "number") {
      return RETRYABLE_SEARCH_STATUSES.has(status);
    }
    return (
      error.code === "ERR_NETWORK" ||
      error.message?.toLowerCase().includes("timeout") === true
    );
  }
  return false;
};

export const invalidateSearchWebsitesPagedCache = () => {
  // Intentionally a no-op right now:
  // search paged requests run without frontend response caching to avoid stale/racey behavior.
};

export const invalidateSearchSidePanelCountsCache = () => {
  thematicCountsRequestCache.clear();
  regionCountsRequestCache.clear();
};

/**
 * Shape of a website search result as returned by the backend.
 * Must match WebsiteSearchResultDto in the Spring Boot backend.
 */
export interface WebsiteSearchResult {
  id: string;
  title: string;
  description: string;
  hostname: string | null;
  urlLandingPage: string | null;
  address: string | null;
  zipCode: string | null;
  addedAt: string | null;
  pageviewsTotal?: number;
}

/**
 * Builds query params for region filtering, ignoring empty entries.
 *
 * @param regionKeys list of region identifiers to forward as `regions`
 * @returns URLSearchParams with one `regions` entry per non-empty key
 */
const buildRegionParams = (regionKeys: string[]) => {
  const params = new URLSearchParams();
  const canonicalRegionKeys = Array.from(
    new Set(
      regionKeys
        .map((key) => key?.trim())
        .filter((key): key is string => !!key)
    )
  ).sort((left, right) => left.localeCompare(right));
  canonicalRegionKeys.forEach((key) => params.append("regions", key));
  return params;
};

/**
 * Calls the backend search endpoint:
 * GET /api/websites/search?query=...
 *
 * @param query search text, trimmed before sending to the backend
 * @param regionKeys optional region filters forwarded as repeated `regions` params
 * @param categorySlugs optional thematic category path (slug per depth) forwarded as slash-separated `categories`
 * @returns list of matching websites; throws when the backend response is not ok
 */
export async function searchWebsites(
  query: string,
  regionKeys: string[] = [],
  categorySlugs: string[] = []
): Promise<WebsiteSearchResult[]> {
  const trimmed = query.trim();
  const regionParams = buildRegionParams(regionKeys);
  const categoryPath = categorySlugs
    .map((slug) => slug?.trim())
    .filter((slug): slug is string => !!slug)
    .slice(0, MAX_CATEGORY_PATH_SEGMENTS);
  const params = new URLSearchParams();

  if (trimmed !== "") {
    params.set("query", trimmed);
  }
  regionParams.forEach((value, key) => params.append(key, value));
  if (categoryPath.length > 0) {
    params.set("categories", categoryPath.join("/"));
  }

  // hard stop: never trigger "empty => all" behavior when no region and no category filter exists
  if (
    trimmed === "" &&
    regionParams.toString() === "" &&
    categoryPath.length === 0
  )
    return [];

  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/websites/search${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await api.get<WebsiteSearchResult[]>(url, {
    headers: { Accept: "application/json", "Cache-Control": "no-store" },
    validateStatus: () => true,
  });

  if (response.status < 200 || response.status >= 300) {
    const serverMessage = (response.data as any)?.message;
    throw new Error(
      typeof serverMessage === "string" && serverMessage.trim()
        ? serverMessage
        : `Search request failed with status ${response.status}`
    );
  }

  return response.data as WebsiteSearchResult[];
}

export interface SearchWebsitesPagedParams {
  query: string;
  regionKeys?: string[];
  categorySlugs?: string[];
  page: number;
  size: number;
  sortBy?: string;
  dateFilter?: string;
}

export interface SearchWebsitesSummaryParams
  extends SearchWebsitesPagedParams {
  facetLayer: string;
  facetState?: string | null;
}

interface RequestOptions {
  signal?: AbortSignal;
}

/**
 * Calls the backend paged search endpoint:
 * GET /api/websites/search/paged?... with exact totals.
 */
export async function searchWebsitesPaged(
  paramsInput: SearchWebsitesPagedParams,
  options: RequestOptions = {}
): Promise<PagedResponse<WebsiteSearchResult>> {
  const regionKeys = paramsInput.regionKeys ?? [];
  const categorySlugs = paramsInput.categorySlugs ?? [];
  const trimmed = (paramsInput.query ?? "").trim();
  const page = Math.max(0, Math.floor(paramsInput.page ?? 0));
  const size = Math.max(1, Math.min(100, Math.floor(paramsInput.size ?? 10)));
  const sortBy =
    (paramsInput.sortBy ?? "relevance").trim().toLowerCase() || "relevance";
  const dateFilter =
    (paramsInput.dateFilter ?? "all").trim().toLowerCase() || "all";

  const regionParams = buildRegionParams(regionKeys);
  const categoryPath = categorySlugs
    .map((slug) => slug?.trim())
    .filter((slug): slug is string => !!slug)
    .slice(0, MAX_CATEGORY_PATH_SEGMENTS);

  const params = new URLSearchParams();
  if (trimmed !== "") {
    params.set("query", trimmed);
  }
  params.set("page", String(page));
  params.set("size", String(size));
  params.set("sortBy", sortBy);
  params.set("dateFilter", dateFilter);
  regionParams.forEach((value, key) => params.append(key, value));
  if (categoryPath.length > 0) {
    params.set("categories", categoryPath.join("/"));
  }

  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/websites/search/paged?${queryString}`;
  for (let attempt = 1; attempt <= SEARCH_PAGED_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await api.get<PagedResponse<WebsiteSearchResult>>(url, {
        headers: { Accept: "application/json", "Cache-Control": "no-store" },
        signal: options.signal,
        validateStatus: () => true,
      });

      if (response.status >= 200 && response.status < 300) {
        const data = response.data as PagedResponse<WebsiteSearchResult>;
        return {
          items: Array.isArray(data?.items) ? data.items : [],
          page: Number(data?.page ?? page),
          size: Number(data?.size ?? size),
          totalElements: Number(data?.totalElements ?? 0),
          totalPages: Number(data?.totalPages ?? 0),
        };
      }

      if (
        attempt < SEARCH_PAGED_MAX_ATTEMPTS &&
        RETRYABLE_SEARCH_STATUSES.has(response.status)
      ) {
        await waitWithOptionalAbort(SEARCH_PAGED_RETRY_DELAY_MS, options.signal);
        continue;
      }

      const serverMessage = (response.data as any)?.message;
      throw new Error(
        typeof serverMessage === "string" && serverMessage.trim()
          ? serverMessage
          : `Paged search request failed with status ${response.status}`
      );
    } catch (error) {
      if (
        attempt < SEARCH_PAGED_MAX_ATTEMPTS &&
        shouldRetryTransportError(error)
      ) {
        await waitWithOptionalAbort(SEARCH_PAGED_RETRY_DELAY_MS, options.signal);
        continue;
      }
      throw error;
    }
  }

  throw new Error("Paged search request failed.");
}

export interface SearchSummaryResponse {
  paged: PagedResponse<WebsiteSearchResult>;
  thematicCounts: ThematicCategoryCountNode[];
  regionCounts: RegionCountDto[];
}

/**
 * Calls the combined summary endpoint:
 * GET /api/websites/search/summary?... with paged + thematic + region counts.
 */
export async function searchWebsitesSummary(
  paramsInput: SearchWebsitesSummaryParams,
  options: RequestOptions = {}
): Promise<SearchSummaryResponse> {
  const regionKeys = paramsInput.regionKeys ?? [];
  const categorySlugs = paramsInput.categorySlugs ?? [];
  const trimmed = (paramsInput.query ?? "").trim();
  const page = Math.max(0, Math.floor(paramsInput.page ?? 0));
  const size = Math.max(1, Math.min(100, Math.floor(paramsInput.size ?? 10)));
  const sortBy =
    (paramsInput.sortBy ?? "relevance").trim().toLowerCase() || "relevance";
  const dateFilter =
    (paramsInput.dateFilter ?? "all").trim().toLowerCase() || "all";
  const facetLayer = (paramsInput.facetLayer ?? "").trim();
  const facetState = (paramsInput.facetState ?? "").trim();
  if (!facetLayer) {
    throw new Error("facetLayer is required.");
  }

  const regionParams = buildRegionParams(regionKeys);
  const categoryPath = categorySlugs
    .map((slug) => slug?.trim())
    .filter((slug): slug is string => !!slug)
    .slice(0, MAX_CATEGORY_PATH_SEGMENTS);

  const params = new URLSearchParams();
  if (trimmed !== "") {
    params.set("query", trimmed);
  }
  params.set("page", String(page));
  params.set("size", String(size));
  params.set("sortBy", sortBy);
  params.set("dateFilter", dateFilter);
  params.set("facetLayer", facetLayer);
  if (facetState) {
    params.set("facetState", facetState);
  }
  regionParams.forEach((value, key) => params.append(key, value));
  if (categoryPath.length > 0) {
    params.set("categories", categoryPath.join("/"));
  }

  const queryString = params.toString();
  const url = `${API_BASE_URL}/api/websites/search/summary?${queryString}`;
  for (let attempt = 1; attempt <= SEARCH_PAGED_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await api.get<SearchSummaryResponse>(url, {
        headers: { Accept: "application/json", "Cache-Control": "no-store" },
        signal: options.signal,
        validateStatus: () => true,
      });

      if (response.status >= 200 && response.status < 300) {
        const data = response.data as SearchSummaryResponse;
        const paged = data?.paged;
        return {
          paged: {
            items: Array.isArray(paged?.items) ? paged.items : [],
            page: Number(paged?.page ?? page),
            size: Number(paged?.size ?? size),
            totalElements: Number(paged?.totalElements ?? 0),
            totalPages: Number(paged?.totalPages ?? 0),
          },
          thematicCounts: Array.isArray(data?.thematicCounts)
            ? data.thematicCounts
            : [],
          regionCounts: Array.isArray(data?.regionCounts) ? data.regionCounts : [],
        };
      }

      if (
        attempt < SEARCH_PAGED_MAX_ATTEMPTS &&
        RETRYABLE_SEARCH_STATUSES.has(response.status)
      ) {
        await waitWithOptionalAbort(SEARCH_PAGED_RETRY_DELAY_MS, options.signal);
        continue;
      }

      const serverMessage = (response.data as any)?.message;
      throw new Error(
        typeof serverMessage === "string" && serverMessage.trim()
          ? serverMessage
          : `Search summary request failed with status ${response.status}`
      );
    } catch (error) {
      if (
        attempt < SEARCH_PAGED_MAX_ATTEMPTS &&
        shouldRetryTransportError(error)
      ) {
        await waitWithOptionalAbort(SEARCH_PAGED_RETRY_DELAY_MS, options.signal);
        continue;
      }
      throw error;
    }
  }

  throw new Error("Search summary request failed.");
}

/**
 * Loads the most recent websites (by addedAt desc).
 *
 * - No fallback to "load all then slice" anymore.
 * - Requires backend endpoint: GET /api/websites/recent?limit=...
 *
 * @param limit maximum results to request (clamped to 1..500)
 * @param regionKeys optional region filters; when present uses search endpoint
 * @returns recent websites; throws when the backend response is not ok
 */
export async function getRecentWebsites(
  limit = 200,
  regionKeys: string[] = []
): Promise<WebsiteSearchResult[]> {
  // Region-aware "recent" is served via the search endpoint with empty query.
  if (regionKeys.length > 0) {
    return searchWebsites("", regionKeys);
  }

  const safeLimit = Math.max(1, Math.min(500, Math.floor(limit)));

  const recentUrl = `${API_BASE_URL}/api/websites/recent?limit=${encodeURIComponent(
    String(safeLimit)
  )}`;

  const res = await api.get<WebsiteSearchResult[]>(recentUrl, {
    headers: { Accept: "application/json", "Cache-Control": "no-store" },
    validateStatus: () => true,
  });

  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Recent request failed with status ${res.status}`);
  }

  return res.data as WebsiteSearchResult[];
}

/**
 * Backend DTO for a thematic category node.
 */
export interface ThematicCategoryDto {
  slug: string;
  nameEn: string;
  nameDe: string;
  iconName?: string | null;
  children?: ThematicCategoryDto[];
}

/**
 * Backend DTO for thematic category counts.
 */
export interface ThematicCategoryCountNode {
  slug: string;
  count: number;
  children?: ThematicCategoryCountNode[];
}

/**
 * Loads the curated thematic category tree from the backend.
 *
 * @returns hierarchical category nodes with localized labels and icon metadata
 */
export async function fetchThematicCategoryTree(): Promise<
  ThematicCategoryDto[]
> {
  const url = `${API_BASE_URL}/api/categories/thematic/tree`;
  const response = await api.get<ThematicCategoryDto[]>(url, {
    headers: { Accept: "application/json" },
    validateStatus: () => true,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      `Failed to load thematic categories (status ${response.status})`
    );
  }
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Loads result counts per thematic category from the backend.
 *
 * @returns hierarchical counts matching the thematic category tree
 */
export async function fetchThematicCategoryCounts(
  regionKeys: string[] = [],
  options: RequestOptions & {
    query?: string | null;
    dateFilter?: string | null;
  } = {}
): Promise<ThematicCategoryCountNode[]> {
  const queryParams = new URLSearchParams();
  const trimmedQuery = options.query?.trim() ?? "";
  const normalizedDateFilter = (options.dateFilter ?? "all").trim() || "all";
  if (trimmedQuery) {
    queryParams.set("query", trimmedQuery);
  }
  queryParams.set("dateFilter", normalizedDateFilter);
  buildRegionParams(regionKeys).forEach((value, key) =>
    queryParams.append(key, value)
  );

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/categories/thematic/counts${
    queryString ? `?${queryString}` : ""
  }`;
  const cacheKey = `thematic:${queryString}`;

  return thematicCountsRequestCache.run({
    key: cacheKey,
    signal: options.signal,
    load: async (requestSignal) => {
      const response = await api.get<ThematicCategoryCountNode[]>(url, {
        headers: { Accept: "application/json" },
        signal: requestSignal,
        validateStatus: () => true,
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          `Failed to load thematic category counts (status ${response.status})`
        );
      }
      return Array.isArray(response.data) ? response.data : [];
    },
  });
}

/**
 * Backend DTO for region density counts.
 */
export interface RegionCountDto {
  regionKey: string;
  count: number;
}

/**
 * Loads aggregated region counts for the map density view.
 */
export async function fetchRegionCounts(params: {
  layer: string;
  query?: string | null;
  categorySlugs?: string[];
  state?: string | null;
  dateFilter?: string | null;
}, options: RequestOptions = {}): Promise<RegionCountDto[]> {
  const trimmedQuery = params.query?.trim() ?? "";
  const categoryPath = (params.categorySlugs ?? [])
    .map((slug) => slug?.trim())
    .filter((slug): slug is string => !!slug)
    .slice(0, MAX_CATEGORY_PATH_SEGMENTS);

  const queryParams = new URLSearchParams();
  queryParams.set("layer", params.layer);
  if (params.state && params.state.trim()) {
    queryParams.set("state", params.state.trim());
  }
  if (trimmedQuery) {
    queryParams.set("query", trimmedQuery);
  }
  if (categoryPath.length > 0) {
    queryParams.set("categories", categoryPath.join("/"));
  }
  queryParams.set("dateFilter", (params.dateFilter ?? "all").trim() || "all");

  const url = `${API_BASE_URL}/api/public/regions/counts?${queryParams.toString()}`;
  const cacheKey = `region:${queryParams.toString()}`;

  return regionCountsRequestCache.run({
    key: cacheKey,
    signal: options.signal,
    load: async (requestSignal) => {
      const response = await api.get<RegionCountDto[]>(url, {
        headers: { Accept: "application/json", "Cache-Control": "no-store" },
        signal: requestSignal,
        validateStatus: () => true,
      });

      if (response.status < 200 || response.status >= 300) {
        const serverMessage = (response.data as any)?.message;
        throw new Error(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : `Region count request failed with status ${response.status}`
        );
      }

      return Array.isArray(response.data) ? response.data : [];
    },
  });
}
